import React, { useState, useMemo } from "react";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { FrontQuestionFormCard } from "../../components/Front/Complex/FrontQuestionFormCard/FrontQuestionFormCard";
import FrontParagraphSmall from "../../components/Front/Simple/FrontParagraphSmall/FrontParagraphSmall";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { FrontSpacing } from "../../models/FrontSpacing";
import { WeightSemiBold } from "../../components/WeightSemiBold/WeightSemiBold";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { FrontFormSectionTitle } from "../../components/Front/Simple/FrontFormSectionTitle/FrontFormSectionTitle";
import { FrontFormPrimaryButton } from "../../components/Front/Simple/FrontFormPrimaryButton/FrontFormPrimaryButton";
import { InlineFlex } from "../../components/BuildingBlocks/InlineFlex";
import { FrontCondensedRadioQuestions } from "../../components/Front/Simple/FrontCondensedRadioQuestions/FrontCondensedRadioQuestions";
import { FrontInputGeneralComponent } from "../../components/Front/Simple/FrontInputGeneralComponent/FrontInputGeneralComponent";
import { FrontDateOfBirthInput } from "../../components/Front/Simple/FrontDateOfBirthInput/FrontDateOfBirthInput";
import { FrontInfoBubble } from "../../components/Front/Simple/FrontInfoBubble/FrontInfoBubble";
import { FrontColors } from "../../models/FrontColors";
import { TOnboardingTabStatus } from "../../components/Front/Simple/FrontOnboardingTab/FrontOnboardingTab";
import { ClientCaseCasePifForm, TClientCaseCasePifForm, TCasePifRequiredSigner } from "../../../../domain/codecs/form/ClientCaseForm";
import { TCasePif1 } from "../../../../domain/codecs/CasePif";
import { pifSections, shouldShowFieldForTitleMode, isLimitedTitleOptionalField, shouldShowSection, shouldShowFreeText, getPifFreeTextKey, getPifFreeTextValue, fieldAllowsDontKnow, PIF_DONT_KNOW_VALUE, TPifField, TPifSection, TPifTitleMode, getInvalidPifFields, isPifFieldValidFormat, TPifMissingField } from "../../../../domain/pif/pifSchema";
import { FrontCheckboxWithLabel } from "../../components/Front/Simple/FrontCheckboxWithLabel/FrontCheckboxWithLabel";
import { FrontInputTextareaComponent } from "../../components/Front/Simple/FrontInputTextareaComponent/FrontInputTextareaComponent";

export const buildPifEditPayload = (
    form: TClientCaseCasePifForm,
    edits: Partial<TCasePif1>,
    clearedDontKnowKeys: ReadonlyArray<string> = [],
): TClientCaseCasePifForm => ({
    ...form,
    children: clearedDontKnowKeys.length === 0
        ? form.children
        : {
            ...form.children,
            auto_answered_keys: form.children.auto_answered_keys
                .filter((k) => !clearedDontKnowKeys.includes(k)),
        },
    edited: {
        ...form.edited,
        ...edits,
    },
});

const fieldDomKey = (key: string) => `pif-field-${key}`;

const FrontCasePifContainer = (props: TContainerStateProps): JSX.Element => {
    const pifForm = props.state.forms.client_case_page.children.case_pif || ClientCaseCasePifForm.newDefault();
    const isStepComplete = pifForm.children.is_step_complete;
    const includeLeasehold = pifForm.children.include_leasehold_section;
    const titleMode: TPifTitleMode = pifForm.children.title_mode;
    const data = pifForm.edited;
    const caseId = props.state.routes.params.caseId;
    const freeText = pifForm.children.free_text;
    const dontKnowKeys = pifForm.children.auto_answered_keys;

    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    const missingFields = useMemo(
        () => getInvalidPifFields(data, includeLeasehold, titleMode, dontKnowKeys),
        [data, includeLeasehold, titleMode, dontKnowKeys],
    );

    const applyEdits = (
        edits: Partial<TCasePif1>,
        clearedDontKnowKeys: ReadonlyArray<string> = [],
    ) => {
        props.dispatch({
            type: "CLIENT_UPDATE_CASE_PIF",
            payload: buildPifEditPayload(pifForm, edits, clearedDontKnowKeys),
        });
    };

    const updateField = (key: keyof TCasePif1, value: boolean | number | string | null) => {
        applyEdits({ [key]: value });
    };

    const updateFreeText = (fieldKey: string, value: string) => {
        props.dispatch({
            type: "CLIENT_UPDATE_CASE_PIF_FREE_TEXT",
            payload: { case_id: caseId, field_key: fieldKey, value },
        });
    };

    const setDontKnow = (fieldKey: string, isDontKnow: boolean) => {
        props.dispatch({
            type: "CLIENT_SET_CASE_PIF_DONT_KNOW",
            payload: { case_id: caseId, field_key: fieldKey, is_dont_know: isDontKnow },
        });
    };

    const updateFieldClearingDontKnow = (key: keyof TCasePif1, value: boolean | number | string | null) => {
        if (!dontKnowKeys.includes(key)) {
            applyEdits({ [key]: value });
            return;
        }
        setDontKnow(key, false);
        applyEdits({ [key]: value }, [key]);
    };

    const updateOccupancy = (value: boolean | number | string | null) => {
        if (value === false) {
            applyEdits({ is_occupied: false, has_tenants: false });
            return;
        }
        applyEdits({ is_occupied: value === true ? true : null });
    };

    const requiredSigners = pifForm.children.required_signers;
    const signedInUserId = pifForm.children.signed_in_user_id;
    const currentSigner = requiredSigners.find((s) => s.user_id === signedInUserId);

    const sign = (signedName: string): boolean => {
        if (missingFields.length > 0) {
            setHasAttemptedSubmit(true);
            const banner = document.getElementById("pif-missing-fields-banner");
            if (banner) banner.scrollIntoView({ behavior: "smooth", block: "start" });
            return false;
        }
        props.dispatch({
            type: "CLIENT_SIGN_CASE_PIF",
            payload: signedName,
        });
        return true;
    };

    const jumpToField = (fieldKey: string) => {
        const el = document.getElementById(fieldDomKey(fieldKey));
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            const focusable = el.querySelector<HTMLElement>("[tabindex], input, button");
            if (focusable) focusable.focus();
        }
    };

    if (isStepComplete) {
        return (
            <FrontQuestionFormCard title="Property Information Form">
                <FrontParagraphSmall>
                    Thanks — we've received your Property Information Form. Your conveyancer will review it and let you know if anything needs following up.
                </FrontParagraphSmall>
            </FrontQuestionFormCard>
        );
    }

    const showErrors = (hasAttemptedSubmit || pifForm.status === "validationError") && missingFields.length > 0;

    return (
        <FrontQuestionFormCard title="Property Information Form">
            <SpacingColumn spacing={FrontSpacing.LARGE_1}>
                <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                    <FrontParagraphSmall>
                        <WeightSemiBold>
                            This form gives the buyer's solicitor detailed information about your property.
                        </WeightSemiBold>
                    </FrontParagraphSmall>
                    <FrontParagraphSmall>
                        Some answers may already be filled in from your case details — please check and edit anything that's wrong. Answer honestly: the buyer can hold you responsible.
                    </FrontParagraphSmall>
                    <FrontParagraphSmall>
                        <WeightBold>You can save as you go and come back later.</WeightBold> When everything's done, hit Submit at the bottom.
                    </FrontParagraphSmall>
                </SpacingColumn>

                {titleMode === "limited" && (
                    <FrontInfoBubble color={FrontColors.PRIMARY_22}>
                        <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                            <FrontParagraphSmall>
                                You're selling as the executor or administrator of an estate, so <WeightSemiBold>we've kept this form short</WeightSemiBold>. We only ask the questions you can reasonably answer.
                            </FrontParagraphSmall>
                            <FrontParagraphSmall>
                                <WeightSemiBold>Anything you leave blank is recorded as "not known"</WeightSemiBold> — the honest, correct answer when you didn't live in the property. Know an answer anyway? Open a section below and add it.
                            </FrontParagraphSmall>
                            <FrontParagraphSmall>
                                <WeightSemiBold>Some questions offer "I don't know"</WeightSemiBold> — choose it if that's the honest answer. We'll look those ones up ourselves.
                            </FrontParagraphSmall>
                        </SpacingColumn>
                    </FrontInfoBubble>
                )}

                {showErrors && (
                    <MissingFieldsBanner
                        missingFields={missingFields}
                        onJumpToField={jumpToField}
                    />
                )}

                {pifSections
                    .filter((section) => shouldShowSection(section, includeLeasehold))
                    .map((section) => (
                        <PifSectionRenderer
                            key={section.key}
                            section={section}
                            data={data}
                            freeText={freeText}
                            titleMode={titleMode}
                            dontKnowKeys={dontKnowKeys}
                            showErrors={showErrors}
                            onUpdateField={updateFieldClearingDontKnow}
                            onUpdateOccupancy={updateOccupancy}
                            onSetDontKnow={setDontKnow}
                            onUpdateFreeText={updateFreeText}
                        />
                    ))}

                <SigningPanel
                    requiredSigners={requiredSigners}
                    currentSigner={currentSigner}
                    onSign={sign}
                    submitFailed={pifForm.status === "validationError" || pifForm.status === "failure" || pifForm.status === "unauthorised"}
                />
            </SpacingColumn>
        </FrontQuestionFormCard>
    );
};

const formatSignedAt = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const SignerStatusLine = (props: { signer: TCasePifRequiredSigner }): JSX.Element => {
    const { signer } = props;
    if (signer.has_current_signature && signer.signed_at !== null) {
        return (
            <FrontParagraphSmall>
                <WeightSemiBold>{signer.display_name}</WeightSemiBold> — signed {formatSignedAt(signer.signed_at)}
            </FrontParagraphSmall>
        );
    }
    if (signer.signed_at !== null) {
        return (
            <FrontParagraphSmall>
                <WeightSemiBold>{signer.display_name}</WeightSemiBold> — previous signature invalidated by later edits, needs to re-sign
            </FrontParagraphSmall>
        );
    }
    return (
        <FrontParagraphSmall>
            <WeightSemiBold>{signer.display_name}</WeightSemiBold> — not yet signed
        </FrontParagraphSmall>
    );
};

const SigningPanel = (props: {
    requiredSigners: TCasePifRequiredSigner[],
    currentSigner: TCasePifRequiredSigner | undefined,
    onSign: (signedName: string) => boolean,
    submitFailed: boolean,
}): JSX.Element => {
    const { requiredSigners, currentSigner, onSign, submitFailed } = props;
    const [typedName, setTypedName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const currentUserHasCurrentSignature = currentSigner?.has_current_signature === true;

    React.useEffect(() => {
        if (isSubmitting && (currentUserHasCurrentSignature || submitFailed)) {
            setIsSubmitting(false);
        }
    }, [isSubmitting, currentUserHasCurrentSignature, submitFailed]);

    const currentUserNeedsToSign = currentSigner !== undefined && !currentSigner.has_current_signature;
    const currentUserAlreadySigned = currentSigner !== undefined && currentSigner.has_current_signature;
    const everyoneSigned = requiredSigners.length > 0 && requiredSigners.every((s) => s.has_current_signature);
    const othersPending = requiredSigners.filter((s) => !s.has_current_signature && s.user_id !== currentSigner?.user_id);

    return (
        <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
            <FrontFormSectionTitle>Sign and submit</FrontFormSectionTitle>

            <FrontInfoBubble color={FrontColors.PRIMARY_22}>
                Everyone on this case needs to sign before the form is submitted to your conveyancer. If anyone edits the answers after signing, their signature is invalidated and they will need to re-sign.
            </FrontInfoBubble>

            <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                {requiredSigners.map((s) => (
                    <SignerStatusLine key={s.user_id} signer={s} />
                ))}
            </SpacingColumn>

            {currentUserNeedsToSign && (
                <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                    {submitFailed && (
                        <FrontInfoBubble color={FrontColors.ERROR_FIREBRICK_22}>
                            <WeightBold>We couldn't submit the form.</WeightBold> Some answers may still be missing or need fixing — please check the questions above, then sign again.
                        </FrontInfoBubble>
                    )}
                    <FrontParagraphSmall>
                        <WeightBold>Type your full name to sign.</WeightBold> By signing, you confirm the answers above are true to the best of your knowledge.
                    </FrontParagraphSmall>
                    <FrontInputGeneralComponent
                        inputType="text"
                        displayError={false}
                        placeholder={currentSigner.display_name}
                        value={typedName}
                        onChange={setTypedName}
                    />
                    <InlineFlex>
                        <FrontFormPrimaryButton
                            icon="done"
                            label="Sign"
                            isDisabled={typedName.trim().length === 0}
                            isLoading={isSubmitting}
                            onClick={() => {
                                setIsSubmitting(true);
                                if (!onSign(typedName.trim())) {
                                    setIsSubmitting(false);
                                }
                            }}
                        />
                    </InlineFlex>
                </SpacingColumn>
            )}

            {currentUserAlreadySigned && !everyoneSigned && (
                <FrontParagraphSmall>
                    <WeightSemiBold>Thanks — you've signed.</WeightSemiBold> Waiting on: {othersPending.map((s) => s.display_name).join(", ")}.
                </FrontParagraphSmall>
            )}

            {currentSigner === undefined && requiredSigners.length > 0 && (
                <FrontParagraphSmall>
                    You can review and edit the answers, but this form needs to be signed by: {requiredSigners.map((s) => s.display_name).join(", ")}.
                </FrontParagraphSmall>
            )}
        </SpacingColumn>
    );
};

const isFieldInvalid = (
    field: TPifField,
    data: TCasePif1,
    titleMode: TPifTitleMode,
    dontKnowKeys: ReadonlyArray<string>,
): boolean => {
    if (!shouldShowFieldForTitleMode(field, data, titleMode)) return false;
    if (fieldAllowsDontKnow(field) && dontKnowKeys.includes(field.key)) return false;
    const v = data[field.key];
    if (field.required) {
        if (field.type === "boolean") return v !== true && v !== false;
        if (field.type === "integer") return typeof v !== "number";
        if (field.type === "decimal") return typeof v !== "number";
        if (field.type === "email") return typeof v !== "string" || v.length === 0 || !isPifFieldValidFormat(field, v);
        if (field.type === "phone") return typeof v !== "string" || v.length === 0 || !isPifFieldValidFormat(field, v);
        return typeof v !== "string" || v.length === 0;
    }
    if (v !== null && v !== undefined && v !== "") {
        return !isPifFieldValidFormat(field, v);
    }
    return false;
};

const MissingFieldsBanner = (props: {
    missingFields: TPifMissingField[],
    onJumpToField: (fieldKey: string) => void,
}): JSX.Element => (
    <div id="pif-missing-fields-banner" className="front-case-pif-missing-fields-banner">
        <SpacingColumn spacing={FrontSpacing.SMALL_3}>
            <FrontParagraphSmall>
                <WeightBold>
                    {props.missingFields.length} {props.missingFields.length === 1 ? "question" : "questions"} still need{props.missingFields.length === 1 ? "s" : ""} answering or correcting before you can submit:
                </WeightBold>
            </FrontParagraphSmall>
            <ul className="front-case-pif-missing-fields-banner__list">
                {props.missingFields.map((m) => (
                    <li key={m.field_key}>
                        <FrontParagraphSmall>
                            <button
                                type="button"
                                className="front-case-pif-missing-fields-banner__jump-button"
                                onClick={() => props.onJumpToField(m.field_key)}
                            >
                                {m.section_title} — {m.field_label}
                            </button>
                        </FrontParagraphSmall>
                    </li>
                ))}
            </ul>
        </SpacingColumn>
    </div>
);

const PifSectionRenderer = (props: {
    section: TPifSection,
    data: TCasePif1,
    freeText: { field_key: string, value: string }[],
    titleMode: TPifTitleMode,
    dontKnowKeys: ReadonlyArray<string>,
    showErrors: boolean,
    onUpdateField: (key: keyof TCasePif1, value: boolean | number | string | null) => void,
    onUpdateOccupancy: (value: boolean | number | string | null) => void,
    onSetDontKnow: (fieldKey: string, isDontKnow: boolean) => void,
    onUpdateFreeText: (fieldKey: string, value: string) => void,
}): JSX.Element | null => {
    const { section, data, freeText, titleMode, dontKnowKeys, showErrors, onUpdateField, onUpdateOccupancy, onSetDontKnow, onUpdateFreeText } = props;

    const renderField = (field: TPifField): JSX.Element => (
        <div key={field.key} id={fieldDomKey(field.key)}>
            <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                <PifFieldRenderer
                    field={field}
                    value={data[field.key]}
                    isDontKnow={dontKnowKeys.includes(field.key)}
                    displayError={showErrors && isFieldInvalid(field, data, titleMode, dontKnowKeys)}
                    onChange={(v) => field.key === "is_occupied"
                        ? onUpdateOccupancy(v)
                        : onUpdateField(field.key, v)}
                    onSetDontKnow={(isDontKnow) => onSetDontKnow(field.key, isDontKnow)}
                />
                {shouldShowFreeText(field, data) && (
                    <PifFreeTextRenderer
                        field={field}
                        value={getPifFreeTextValue(freeText, getPifFreeTextKey(field))}
                        onChange={(v) => onUpdateFreeText(getPifFreeTextKey(field), v)}
                    />
                )}
            </SpacingColumn>
        </div>
    );

    const shownFields = section.fields.filter((f) => shouldShowFieldForTitleMode(f, data, titleMode));
    const optionalFields = titleMode === "limited"
        ? section.fields.filter((f) => isLimitedTitleOptionalField(f, data))
        : [];

    if (shownFields.length === 0 && optionalFields.length === 0) {
        return null;
    }

    return (
        <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
            <FrontFormSectionTitle>{section.title}</FrontFormSectionTitle>
            <FrontInfoBubble color={FrontColors.PRIMARY_22}>
                {section.description}
            </FrontInfoBubble>
            {shownFields.length > 0 && (
                <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                    {shownFields.map(renderField)}
                </SpacingColumn>
            )}
            {optionalFields.length > 0 && (
                <LimitedTitleOptionalBlock count={optionalFields.length}>
                    <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                        {optionalFields.map(renderField)}
                    </SpacingColumn>
                </LimitedTitleOptionalBlock>
            )}
        </SpacingColumn>
    );
};

const LimitedTitleOptionalBlock = (props: React.PropsWithChildren<{ count: number }>): JSX.Element => {
    const [expanded, setExpanded] = useState(false);
    return (
        <SpacingColumn spacing={FrontSpacing.SMALL_2}>
            <button
                type="button"
                className="front-case-pif-optional-toggle"
                onClick={() => setExpanded((e) => !e)}
            >
                <FrontParagraphSmall>
                    {expanded ? "▾ " : "▸ "}
                    <WeightSemiBold>Not known — sold by personal representative.</WeightSemiBold>{" "}
                    {props.count} optional {props.count === 1 ? "question" : "questions"} you can answer if you know.
                </FrontParagraphSmall>
            </button>
            {expanded && props.children}
        </SpacingColumn>
    );
};

const DONT_KNOW_LABEL = "I don't know";

const PifFieldRenderer = (props: {
    field: TPifField,
    value: unknown,
    isDontKnow: boolean,
    displayError: boolean,
    onChange: (v: boolean | number | string | null) => void,
    onSetDontKnow: (isDontKnow: boolean) => void,
}): JSX.Element => {
    const { field, value, isDontKnow, displayError, onChange, onSetDontKnow } = props;
    const allowsDontKnow = fieldAllowsDontKnow(field);

    if (field.type === "boolean") {
        if (allowsDontKnow) {
            return (
                <FrontCondensedRadioQuestions<string>
                    label={field.label}
                    value={isDontKnow ? PIF_DONT_KNOW_VALUE : (typeof value === "boolean" ? String(value) : null)}
                    options={[
                        { value: "true", label: "Yes" },
                        { value: "false", label: "No" },
                        { value: PIF_DONT_KNOW_VALUE, label: DONT_KNOW_LABEL },
                    ]}
                    onChange={(v) => v === PIF_DONT_KNOW_VALUE
                        ? onSetDontKnow(true)
                        : onChange(v === "true")}
                    isEditable={true}
                    displayValidationErrorsIfPresent={displayError}
                    guidance={field.helpText ? <FrontParagraphSmall>{field.helpText}</FrontParagraphSmall> : undefined}
                />
            );
        }
        return (
            <FrontCondensedRadioQuestions<boolean>
                label={field.label}
                value={typeof value === "boolean" ? value : null}
                options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                ]}
                onChange={onChange}
                isEditable={true}
                displayValidationErrorsIfPresent={displayError}
                guidance={field.helpText ? <FrontParagraphSmall>{field.helpText}</FrontParagraphSmall> : undefined}
            />
        );
    }

    if (field.type === "select" && field.options) {
        return (
            <FrontCondensedRadioQuestions<string>
                label={field.label}
                value={isDontKnow ? PIF_DONT_KNOW_VALUE : (typeof value === "string" ? value : null)}
                options={[
                    ...field.options.map((o) => ({ value: o.value, label: o.label })),
                    ...(allowsDontKnow ? [{ value: PIF_DONT_KNOW_VALUE, label: DONT_KNOW_LABEL }] : []),
                ]}
                onChange={(v) => v === PIF_DONT_KNOW_VALUE ? onSetDontKnow(true) : onChange(v)}
                isEditable={true}
                displayValidationErrorsIfPresent={displayError}
                guidance={field.helpText ? <FrontParagraphSmall>{field.helpText}</FrontParagraphSmall> : undefined}
            />
        );
    }

    const dontKnowToggle = allowsDontKnow
        ? (
            <FrontCheckboxWithLabel
                isSelected={isDontKnow}
                label={DONT_KNOW_LABEL}
                onClick={() => onSetDontKnow(!isDontKnow)}
            />
        )
        : null;

    if (field.type === "integer") {
        return (
            <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                <FrontParagraphSmall><WeightSemiBold>{field.label}</WeightSemiBold></FrontParagraphSmall>
                {field.helpText && <FrontParagraphSmall>{field.helpText}</FrontParagraphSmall>}
                {!isDontKnow && (
                    <FrontInputGeneralComponent
                        inputType="numericString"
                        displayError={displayError}
                        value={typeof value === "number" ? String(value) : ""}
                        onChange={(s) => onChange(s === "" ? null : Number(s))}
                    />
                )}
                {dontKnowToggle}
            </SpacingColumn>
        );
    }

    if (field.type === "date") {
        return (
            <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                <FrontParagraphSmall><WeightSemiBold>{field.label}</WeightSemiBold></FrontParagraphSmall>
                {field.helpText && <FrontParagraphSmall>{field.helpText}</FrontParagraphSmall>}
                {!isDontKnow && (
                    <FrontDateOfBirthInput
                        value={typeof value === "string" ? value : null}
                        onChange={(v) => onChange(v)}
                        hasError={displayError}
                    />
                )}
                {dontKnowToggle}
            </SpacingColumn>
        );
    }

    if (field.type === "email") {
        return (
            <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                <FrontParagraphSmall><WeightSemiBold>{field.label}</WeightSemiBold></FrontParagraphSmall>
                {field.helpText && <FrontParagraphSmall>{field.helpText}</FrontParagraphSmall>}
                <FrontInputGeneralComponent
                    inputType="email"
                    displayError={displayError}
                    placeholder={field.placeholder}
                    value={typeof value === "string" ? value : ""}
                    onChange={(s) => onChange(s === "" ? null : s)}
                />
            </SpacingColumn>
        );
    }

    if (field.type === "phone") {
        return (
            <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                <FrontParagraphSmall><WeightSemiBold>{field.label}</WeightSemiBold></FrontParagraphSmall>
                {field.helpText && <FrontParagraphSmall>{field.helpText}</FrontParagraphSmall>}
                <FrontInputGeneralComponent
                    inputType="tel"
                    displayError={displayError}
                    placeholder={field.placeholder}
                    value={typeof value === "string" ? value : ""}
                    onChange={(s) => onChange(s === "" ? null : s)}
                />
            </SpacingColumn>
        );
    }

    return (
        <SpacingColumn spacing={FrontSpacing.SMALL_2}>
            <FrontParagraphSmall><WeightSemiBold>{field.label}</WeightSemiBold></FrontParagraphSmall>
            {field.helpText && <FrontParagraphSmall>{field.helpText}</FrontParagraphSmall>}
            {!isDontKnow && (
                <FrontInputGeneralComponent
                    inputType="text"
                    displayError={displayError}
                    placeholder={field.placeholder}
                    value={typeof value === "string" ? value : ""}
                    onChange={(s) => onChange(s === "" ? null : s)}
                />
            )}
            {dontKnowToggle}
        </SpacingColumn>
    );
};

const PifFreeTextRenderer = (props: {
    field: TPifField,
    value: string,
    onChange: (v: string) => void,
}): JSX.Element => {
    // The default prompt must match the reveal direction: a few questions reveal
    // the details box on "No" (e.g. non-standard construction, heating not in
    // working order) — showing "If yes" under a No reads as a bug.
    const prompt = props.field.freeTextPrompt
        ?? (props.field.freeTextWhen?.value === false
            ? "If no, please give details"
            : "If yes, please give details");
    return (
        <SpacingColumn spacing={FrontSpacing.SMALL_1}>
            <FrontParagraphSmall><WeightSemiBold>{prompt}</WeightSemiBold></FrontParagraphSmall>
            <FrontInputTextareaComponent
                value={props.value}
                onChange={props.onChange}
            />
        </SpacingColumn>
    );
};

export const getCasePifStatus = (props: TContainerStateProps): TOnboardingTabStatus => {
    const pifForm = props.state.forms.client_case_page.children.case_pif;
    if (!pifForm) return "active";
    if (pifForm.children.is_step_complete) return "completed";
    return "active";
};

export default FrontCasePifContainer;
